import * as React from 'react';
import bindAllOfThis from 'src/utils/BindThisHelper';
import Routes from 'src/core/Routes';
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import StudentActivityDTO from "src/models/activity/StudentActivityDTO";
import StudentActivityUserQuestionResponseDTO from "src/models/activity/StudentActivityUserQuestionResponseDTO";
import { Game } from "src/constants/Games";
import StudentActivityQuestionDTO from "src/models/activity/StudentActivityQuestionDTO";
import ActivityQuestionDisplay from "src/components/student/ActivityQuestionDisplay";
import ArrayUtil from "src/utils/ArrayUtil";
import { Button } from "antd";
import DebugUtil from "src/utils/DebugUtil";
import { Track } from 'src/providers/TelemetryService';

interface OrderedCategoryQuestion {
    order: number;
    categoryIndex: number;
    questionIndex: number;
    question: StudentActivityQuestionDTO;
}

interface ActivityQuestionProps extends LocalizeContextProps {
    activity: StudentActivityDTO;
    currentGame: Game;
    isSubmitting: boolean;
    onBack: () => void;

    studentResponses: StudentActivityUserQuestionResponseDTO[];
    onFinish: (studentResponses: StudentActivityUserQuestionResponseDTO[]) => void;
}

interface ActivityQuestionState {
    step: number;
    isLoading: boolean;
    sortedQuestions: OrderedCategoryQuestion[];
    isCategoryAudioComplete: boolean;
    studentResponses: StudentActivityUserQuestionResponseDTO[];
}

class ActivityQuestion extends React.Component<ActivityQuestionProps, ActivityQuestionState> {
    constructor(props: ActivityQuestionProps) {
        super(props);
        bindAllOfThis(this, ActivityQuestion.prototype);

        this.state = {
            step: 0,
            isLoading: false,
            isCategoryAudioComplete: false,
            sortedQuestions: [],
            studentResponses: [],
        };
    }

    componentDidMount() {
        this.reloadComponent(this.props);
        const { activity, currentGame } = this.props;
        const activityType = activity.isSurvey ? 'Survey' : 'Quiz';
        const activityName = `${currentGame.name}_${currentGame.grade.shortName}${currentGame.planet.name}_${activityType}`;

        const { step } = this.state;

        Track.Activity(activity.isSurvey ? 'SURVEY' : 'QUIZ', activityName, activity.id, 'QUESTION', step);
    }

    componentDidUpdate(prevProps, prevState): void {
        const { activity: currentActivity, currentGame: currentCurrentGame } = this.props;
        const { activity: prevActivity, currentGame: prevCurrentGame } = prevProps;

        const { step: currentStep } = this.state;
        const { step: prevStep } = prevState;

        if(prevActivity.id !== currentActivity.id || prevStep !== currentStep) {
            const activityType = currentActivity.isSurvey ? 'Survey' : 'Quiz';
            const activityName = `${currentCurrentGame.name}_${currentCurrentGame.grade.shortName}${currentCurrentGame.planet.name}_${activityType}`;

            Track.Activity(currentActivity.isSurvey ? 'SURVEY' : 'QUIZ', activityName, currentActivity.id, 'QUESTION', currentStep);
        }
    }

    componentWillReceiveProps(nextProps: ActivityQuestionProps) {
        if ((this.props.activity == null && nextProps.activity != null) || (this.props.activity.id !== nextProps.activity.id)) {
            this.reloadComponent(nextProps);
        }
    }

    private reloadComponent(props: ActivityQuestionProps) {
        // Sort the questions
        let items = props.activity.questions.sort((first, second) => first.displayOrder - second.displayOrder);

        let categoryIndex = 0;
        let questionIndex = 0;
        let currentIndex = 0;
        let currentCategory: any = items[0].category;
        let fooItems = items.map<OrderedCategoryQuestion>(item => {
            if (item.category.id !== currentCategory.id) {
                currentCategory = item.category;
                categoryIndex++;
                questionIndex = 0;
            }
            let result = {
                order: currentIndex,
                categoryIndex: categoryIndex,
                questionIndex: questionIndex,
                question: item
            };

            currentIndex++;
            questionIndex++;
            return result;
        });

        this.setState({
            sortedQuestions: fooItems,
            isLoading: false,
            studentResponses: props.studentResponses.length > 0 ? props.studentResponses : this.props.studentResponses,
            step: 0,
        });
    }

    private onPrev() {
        let newStep = this.state.step - 1;

        // 0 is the lower limit
        if (newStep < 0) {
            this.props.onBack();
            return;
        }

        this.setState({
            step: newStep,
        });
    }

    private onNext() {
        let newStep = this.state.step + 1;

        if (newStep > this.props.activity.questions.length - 1) {
            // Submit after validating
            if (this.validate()) {
                this.props.onFinish(this.state.studentResponses);
                return;
            }
        }

        return this.setState({
            step: newStep,
        });
    }

    private updateStudentResponses(questionId: number, responseIds: number[]) {
        let otherResponses = this.state.studentResponses.filter(x => x.activityQuestionId !== questionId);
        let newResponses = responseIds.map<StudentActivityUserQuestionResponseDTO>(x => {
            return {
                activityQuestionId: questionId,
                activityQuestionResponseId: x,
            } as StudentActivityUserQuestionResponseDTO;
        });

        this.setState({
            studentResponses: [...otherResponses, ...newResponses]
        });
    }

    private validate(): boolean {
        if (this.props.activity == null || this.state.sortedQuestions == null || this.state.sortedQuestions.length < 1) {
            return false;
        }

        // Check if all questions have a response attached to them
        return this.state.sortedQuestions.every(x => this.state.studentResponses.findIndex(y => y.activityQuestionId === x.question.id) > -1);
    }

    private debugRandom() {
        let newResponses = this.state.sortedQuestions.map(x => x.question).map(question => {
            let max = question.responses.length;
            // For 4 responses, we should get a spread from 0 - 3 but never quite making it to 4, which is good on a 0-indexed array
            let index = Math.floor(Math.random() * max);
            let response = question.responses[index];
            return {
                activityQuestionId: question.id,
                activityQuestionResponseId: response.id
            } as StudentActivityUserQuestionResponseDTO;
        });

        this.setState({ studentResponses: newResponses });
    }

    private debugGotoEnd() {
        this.setState({ step: this.props.activity.questions.length - 1 });
    }

    render() {
        if (this.props.activity == null || this.props.activity.id < 1) {
            return null;
        }

        let step = this.state.step;
        // let canLeft = step > 0;
        let canLeft = true;
        let canRight = step < this.props.activity.questions.length - 1;
        let isValid = this.validate();
        let isSubmitting = this.props.isSubmitting;
        let currentQuestion = this.state.sortedQuestions.find(x => x.question.displayOrder === this.state.step + 1);
        let studentResponse = this.state.studentResponses.filter(x => x.activityQuestionId === currentQuestion.question.id);

        return [
            DebugUtil.isDebugEnabled() && <Button tabIndex={-1} key="goFrontDoorYourSelf" type="primary" onClick={this.debugRandom}>DEBUG: Fill Randomly</Button>,
            DebugUtil.isDebugEnabled() && <Button tabIndex={-1} key="goSideDoorYourSelf" type="primary" onClick={this.debugGotoEnd}>DEBUG: Step To End</Button>,
            <ActivityQuestionDisplay
                key="QuestionDisplay"
                currentGame={this.props.currentGame}
                onNext={this.onNext}
                onPrev={this.onPrev}
                updateStudentResponses={this.updateStudentResponses}
                canLeft={canLeft}
                canRight={canRight}
                currentQuestion={currentQuestion}
                studentResponses={studentResponse}
                isSurvey={this.props.activity.isSurvey}
                isValid={isValid}
                isSubmitting={isSubmitting}
            />
        ];
    }
}

export default withLocalize(ActivityQuestion);



// WEBPACK FOOTER //
// ./src/components/student/ActivityQuestion.tsx